import {
  ENVIRONMENT_TOKEN,
  IInitService,
  LOGGING_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, from, Observable, of, throwError } from 'rxjs';
import { LocalStorageService } from '../browser/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService implements IInitService {
  protected environment = inject(ENVIRONMENT_TOKEN);
  protected http = inject(HttpClient);
  protected localStorage = inject(LocalStorageService);
  protected loggingService = inject(LOGGING_SERVICE_TOKEN, { optional: true });
  public cache?: boolean;

  public api_url: string = this.environment.microservices_api_url;

  init() {
    return of(null);
  }

  public get = (
    route: string,
    additionalParamsMap?: Map<string, any>,
    useCache?: boolean,
  ): Observable<any> => {
    let data = useCache ? this.localStorage.get(route) : undefined;
    if (!data) {
      if (useCache) {
        this.http
          .get(
            this.resolveRoute(route, this.api_url),
            this.generateOptions(additionalParamsMap),
          )
          .subscribe((dto) => {
            data = dto;
            this.localStorage.set(route, data);
            return of(data);
          });
      } else {
        return this.http
          .get(
            this.resolveRoute(route, this.api_url),
            this.generateOptions(additionalParamsMap),
          )
          .pipe(
            catchError((error) => {
              this.loggingService?.handleError({
                title: 'API Service Get Error',
                message: error.message,
                extra: error,
              });
              return throwError(() => error);
            }),
          );
      }
    } else {
      return of(data);
    }
    return of(undefined);
    //return data;
  };

  public put = (route: string, body: any = null): Observable<any> => {
    return this.http
      .put(this.resolveRoute(route, this.api_url), body, this.generateOptions())
      .pipe(
        catchError((error) => {
          this.loggingService?.handleError({
            title: 'API Service Put Error',
            message: error.message,
            extra: { error, body },
          });
          return throwError(() => error);
        }),
      );
  };

  public post = (
    route: string,
    body: any = null,
    additionalParamsMap?: Map<string, any>,
  ): Observable<any> => {
    return this.http
      .post(this.resolveRoute(route, this.api_url), body, {
        ...this.generateOptions(additionalParamsMap),
      })
      .pipe(
        catchError((error) => {
          this.loggingService?.handleError({
            title: 'API Service Post Error',
            message: error.message,
            extra: { error, body },
          });
          return throwError(() => error);
        }),
      );
  };

  public postFile = (
    route: string,
    file: File | FileList,
    additionalParamsMap?: Map<string, any>,
  ): Observable<any> => {
    const formData: FormData = new FormData();
    if (file instanceof FileList) {
      for (let i = 0; i < file.length; i++) {
        formData.append('file' + i, file.item(i) as File, file.item(i)?.name);
      }
    } else {
      formData.append('file', file, file.name);
    }
    return this.http
      .post(this.resolveRoute(route, this.api_url), formData, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          let files = file instanceof FileList ? Array.from(file) : [file];
          let fileBuffers$ = files?.map((f) => from(f.arrayBuffer()));

          forkJoin(fileBuffers$).subscribe((buffers) => {
            this.loggingService?.handleError({
              title: 'API Service Post File Error',
              message: error.message,
              extra: { error },
              attachments: files.map((f, i) => ({
                filename: f.name,
                data: buffers.at(i)
                  ? new Uint8Array(buffers!.at(i)!)
                  : 'undefined',
                contentType: f.type,
              })),
            });
          });

          return throwError(() => error);
        }),
      );
  };

  public getBlob = (route: string): Observable<Blob> => {
    return this.http
      .get(this.resolveRoute(route, this.api_url), {
        withCredentials: true,
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          this.loggingService?.handleError({
            title: 'API Service Get Blob Error',
            message: error.message,
            extra: error,
          });
          return throwError(() => error);
        }),
      );
  };

  public delete = (route: string): Observable<any> => {
    return this.http
      .delete(this.resolveRoute(route, this.api_url), this.generateOptions())
      .pipe(
        catchError((error) => {
          this.loggingService?.handleError({
            title: 'API Service Delete Error',
            message: error.message,
            extra: { error },
          });
          return throwError(() => error);
        }),
      );
  };

  private resolveRoute(route: string, envURI: string) {
    return `${envURI}/${route}`;
  }

  private generateOptions(additionalParamsMap?: Map<string, any>) {
    var options: {
      withCredentials?: boolean;
      params?: HttpParams;
      headers?: HttpHeaders;
    } = {};
    options.withCredentials = true;

    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Response-Type': 'text',
    };
    const additionalHeaders = additionalParamsMap
      ? Object.fromEntries(additionalParamsMap)
      : {};

    const headers = { ...defaultHeaders, ...additionalHeaders };

    if (!additionalHeaders['IsFile'])
      options.headers = new HttpHeaders(headers);

    return options;
  }
}
